import { types, flow, getEnv } from 'mobx-state-tree';
import { ProgressStateModelForStore, ProgressStateModel } from './progressStateModel';
import { IGroupedApiCtx } from '../api/apiCommon';
import { ISportsBettingBaseApi, errorIsCancel } from '../api/apiBase';
import { VideoUrlsResponse, VideoUrlDetail } from '../dtos/videoUrlsDtos';
import { ICommonAppEnvWithSBBaseApi } from './index';
import { translation, dateFormatter, TimeFormat, DateFormat, getCurrentLocale } from 'easit--localization';
import { ILoggingService, gAService } from 'eTip--common';
import { ITelemetryService, TelemetryServiceDataType } from '../services/telemetryService';

export enum LiveStreamOperatorType { None = "none", MatchTrackerBR = "matchTracker", MatchTrackerBRStage = "matchTrackerStage", BRVideo = "bRVideo" };

export const LiveStreamOperatorTypeForStore = types.enumeration('LiveStreamOperatorTypeForStore', [LiveStreamOperatorType.None, LiveStreamOperatorType.MatchTrackerBR, LiveStreamOperatorType.MatchTrackerBRStage, LiveStreamOperatorType.BRVideo]);

export enum LiveStreamErrorType { General = "general", InitPlayer = "initPlayer", NotLogged = "notLogged", NoToken = "noToken", StreamFirstLoad = "streamFirstLoad", StreamErrorLater = "streamErrorLater", NotAvailable = "notAvailable", NotAvailablePart = "notAvailablePart", LSAccessDenied = "LSAccessDenied" };

export const LiveStreamErrorTypeForStore = types.enumeration('LiveStreamErrorTypeForStore', [LiveStreamErrorType.General, LiveStreamErrorType.InitPlayer, LiveStreamErrorType.NotLogged, LiveStreamErrorType.NoToken, LiveStreamErrorType.StreamFirstLoad, LiveStreamErrorType.StreamErrorLater, LiveStreamErrorType.NotAvailable, LiveStreamErrorType.NotAvailablePart, LiveStreamErrorType.LSAccessDenied]);

export enum LiveStreamDisciplineType { None = "0", Some = "1", Tennis = "2" };
export const LiveStreamDisciplineTypeForStore = types.enumeration("LiveStreamDisciplineTypeForStore", [LiveStreamDisciplineType.None, LiveStreamDisciplineType.Some, LiveStreamDisciplineType.Tennis]);

export const LiveStreamInApi = types.model({
    operator: LiveStreamOperatorTypeForStore, //use LiveStreamOperatorType
    externalId: types.string,
    startDate: types.maybeNull(types.Date)
}).actions(self => {
    const setData = (operator: LiveStreamOperatorType, externalId: string, startDate: Date | null): void => {
        self.operator = operator;
        self.externalId = externalId;
        self.startDate = startDate;
    }

    return {
        setData
    }
});

/*const LiveStreamPlayerMonitor = types.model({
}).actions(self => {
    const play: (matchId: string, eventName: string) => void = ();
    stop: () => void;
})*/

export interface ILiveStreamPlayerMonitor {
    play: (matchId: string, eventName: string, operator: LiveStreamOperatorType) => void;
    stop: (matchId: string, operator: LiveStreamOperatorType) => void;
    clearPlayedMatch: () => void;
}

const addEventStartToMessage = (eventStart: Date, message: string): string => {
    var strToReplace = "";
    if (eventStart) {
        strToReplace = dateFormatter.formatDateTimeAsRelative(eventStart, DateFormat.DDMM, TimeFormat.HHMM);
    }
    return message.replace("%1", strToReplace);
};

/*
    Store for streams and matchtracker together. Better to separate matchtracker and add matchTrackerKey there. Now matchTrackerKey is provided by event detail stores.
*/
export const LiveStream = types.model({
    operator: LiveStreamOperatorTypeForStore,
    eventStart: types.maybeNull(types.Date),
    state: ProgressStateModelForStore,
    errorReason: types.maybeNull(LiveStreamErrorTypeForStore),
    externalId: types.maybeNull(types.string), //TODOL in devel no value but it should be
    eventId: types.number,
    eventName: types.string,
    eventDiscipline: LiveStreamDisciplineTypeForStore,
    eventCategoryPath: types.maybeNull(types.string), // for livestream expected not null
    graphicsPresentationId: types.maybeNull(types.number),
    videoUrlHls: types.maybeNull(types.string),
    videoUrlDash: types.maybeNull(types.string),
})
    .views(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        return {

            get languageId(): string {
                return getCurrentLocale();
            },

            get tokenForPopup(): string {
                return env.userToken;
            },

            get errorMessage(): string {
                switch (self.errorReason) {
                    case LiveStreamErrorType.NotLogged: return translation().liveStream_NeedToLogin; break;
                    case LiveStreamErrorType.LSAccessDenied: return translation().liveStream_NeedLiveStreamAccess; break;
                    case LiveStreamErrorType.NotAvailable: return addEventStartToMessage(self.eventStart, translation().liveStream_NotPlayedNow); break;
                    case LiveStreamErrorType.NotAvailablePart: return translation().liveStream_NoVideoForThisPart; break;
                    case LiveStreamErrorType.StreamFirstLoad: return addEventStartToMessage(self.eventStart, translation().liveStream_NotAvailableFormat); break;
                    default: return translation().liveStream_ErrorOpening;
                }
            },

            get errorCanShowTryAgain(): boolean {
                return self.errorReason !== LiveStreamErrorType.NotLogged;
            },

            get errorCanShowLoginAfterMessage(): boolean {
                return self.errorReason === LiveStreamErrorType.NotLogged;
            },

            get loginUrl(): string {
                return env.settings.loginUrl + "?urlref=" + encodeURIComponent(window.document.location.href);
            },

            get loggingService() {
                return env.loggingService;
            }
        }
    })
    .actions(self => {

        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        const monitor = env.liveStreamMonitorFactory();

        let synchroCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
        const prepareUrlsAsync: () => Promise<void> = flow(function* () {
            if (self.operator == LiveStreamOperatorType.MatchTrackerBR || self.operator == LiveStreamOperatorType.MatchTrackerBRStage) {
                self.state = ProgressStateModel.Success;
                return;
            }
            if (self.operator == LiveStreamOperatorType.BRVideo) {

                if (env.settings.liveStreamUA === 0 && env.loggedUserId <= 0
                    || env.settings.liveStreamUA > 0 && env.settings.liveStreamUA !== env.loggedUserId) {

                    self.state = ProgressStateModel.Failed;
                    self.errorReason = LiveStreamErrorType.NotLogged;
                    return;
                }

                if (env.player && !env.player.Permissions_LiveStream) {
                    self.state = ProgressStateModel.Failed;
                    self.errorReason = LiveStreamErrorType.LSAccessDenied;
                    return;
                }

                let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
                try {
                    if (synchroCtx && synchroCtx.isOpen()) {
                        synchroCtx.cancel('Load has been canceled.');
                        synchroCtx = null;
                    }
                    ctx = env.api.open();
                    synchroCtx = ctx;

                    self.state = ProgressStateModel.Loading;
                    let r: VideoUrlsResponse = yield synchroCtx.api.getVideoUrlsAsync({ streamId: self.externalId });
                    /*TODOLS demo videos * /
                    let r = <VideoUrlsResponse>{
                        UrlDash: ["https://sportradar-lco-ipv4.akamaized.net/151/648854/dash/desktop/tennis_itf_men_itf_portugal_f11_men_singles_648854_new.mpd?cid=151&mid=23865685&ecid=648854&pid=2&dtid=1&sid=458440573721&hdnts=ip=85.93.127.19~exp=1602150208~acl=%2F151%2F648854%2Fdash%2Fdesktop%2F%2A~hmac=ee97a84af2a7d2829227b951ad78feff9bb36a8cf398a8356f6d8eb11dd0057d"],
                        UrlHls: ["https://sportradar-lco-ipv4.akamaized.net/151/648854/mobile/tennis_itf_men_itf_portugal_f11_men_singles_648854_new_512k.m3u8?cid=151&mid=23865685&ecid=648854&pid=2&dtid=1&sid=583987734407&hdnts=ip=85.93.127.19~exp=1602151110~acl=%2F151%2F648854%2Fmobile%2F%2A~hmac=9d5400f1fcb149b490572a994e7da021bbcb4ac46965e89b59e919125e0a68a5"]
                    };
                    //*/
                    if (r && (r.UrlHls || r.UrlDash)) {
                        self.videoUrlHls = getFirstItemUrlIfNotNull(r.UrlHls);
                        self.videoUrlDash = getFirstItemUrlIfNotNull(r.UrlDash);
                        self.state = ProgressStateModel.Success;
                    } else {
                        self.state = ProgressStateModel.Failed;
                        self.errorReason = LiveStreamErrorType.NotAvailable;
                    }
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    self.state = ProgressStateModel.Failed;
                    throw error;
                } finally {
                    if (synchroCtx === ctx) {
                        synchroCtx = null;
                    }
                }
                return;
            }
            throw new Error("Unexpected operator " + self.operator);
        });

        const setData = (params: { operator: LiveStreamOperatorType, externalId: string, eventId: number, eventName: string, eventDiscipline: LiveStreamDisciplineType, eventCategoryPath: string, eventStart?: Date, graphicsPresentationId: number }): void => {
            if (self.operator != params.operator || self.eventId != params.eventId) {

                videoStoppedForMonitoring();

                self.operator = params.operator;
                self.externalId = params.externalId;
                self.eventId = params.eventId;
                self.eventName = params.eventName;
                self.eventCategoryPath = params.eventCategoryPath;
                self.eventDiscipline = params.eventDiscipline;
                self.eventStart = params.eventStart;
                self.state = ProgressStateModel.NotInitialized;
                self.errorReason = null;
                self.graphicsPresentationId = params.graphicsPresentationId;
            }
        }

        const setErrorWhenShown = (errorReason: LiveStreamErrorType): void => {
            self.state = ProgressStateModel.Failed;
            self.errorReason = errorReason;
        }

        const getFirstItemUrlIfNotNull = (values: VideoUrlDetail[]): string | null => {
            if (values && values.length) {

                let item = values[0];
                if (item.StreamName && item.Url) {
                    let posOfQuest = item.Url.indexOf("?");
                    if (posOfQuest > 0) {
                        return item.Url.substring(0, posOfQuest) + item.StreamName + item.Url.substring(posOfQuest);
                    }

                    return item.Url + item.StreamName;
                }
                return item.Url;
            }
            return null;
        }

        const hide = (): void => {
            if (self.state === ProgressStateModel.Success) {
                videoStoppedForMonitoring();
            }
            self.state = ProgressStateModel.NotInitialized;
        }

        const videoShownForMonitoring = (): void => {
            if (self.operator == LiveStreamOperatorType.MatchTrackerBR) {
                self.state = ProgressStateModel.Success;  //no Get urls is called
            }
            monitor.play(self.eventId.toString(), self.eventName, self.operator);
        }

        const videoStoppedForMonitoring = (): void => {
            if (self.state !== ProgressStateModel.Success) {
                return;
            }
            monitor.stop(self.eventId.toString(), self.operator);
        }

        return {
            prepareUrlsAsync,
            setData,
            setErrorWhenShown,
            hide,
            videoShownForMonitoring,
            videoStoppedForMonitoring
        }
    });

export class LiveStreamMonitor implements ILiveStreamPlayerMonitor {

    private matchId: string;
    private startedTimeTs: number;
    private lastHeartbeatTs: number;
    private eventName: string;
    private operator: LiveStreamOperatorType;
    private checkTimer: number;
    private loggingService: ILoggingService;
    private telemetryService: ITelemetryService;

    public constructor(loggingService: ILoggingService, telemetryService: ITelemetryService, doMonitorWindowClose: boolean) {
        this.loggingService = loggingService;
        this.telemetryService = telemetryService;

        if (doMonitorWindowClose) {
            // throw new Error("Not implemented");
            // recommendation from web vitals (https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event)
            // also reason that it does show correct values in GA with pageUnload
            // if (document.onvisibilitychange) {
            //     document.addEventListener("visibilitychange", () => {    // leaks
            //         console.log("Page visibility change ", document.visibilityState);
            //         if (document.visibilityState === 'hidden') {
            //             if (this.startedTimeTs) {
            //                 this.stopRaw(new Date().getTime(), true);
            //             }
            //         }
            //     });
            //     return;
            // }

            // window.addEventListener("pagehide", () => {   // leaks
            //     console.log("Page pagehide");
            //     if (this.startedTimeTs) {
            //         this.stopRaw(new Date().getTime(), true);
            //     }
            // });
        }
    }

    public clearPlayedMatch(): void {
        this.matchId = null;
        this.startedTimeTs = null;
        this.lastHeartbeatTs = null;
        this.eventName = null;
        this.operator = null;
        this.checkTimer = null;
    }

    public play(matchId: string, eventName: string, operator: LiveStreamOperatorType): void {
        if (this.matchId === matchId && this.operator == operator) {
            return;
        }
        this.stop(this.matchId, this.operator);

        this.matchId = matchId;
        this.startedTimeTs = new Date().getTime();
        this.lastHeartbeatTs = this.startedTimeTs;
        this.eventName = eventName;
        this.operator = operator;

        this.loggingService.info(`Ga event open ${this.eventName} ${this.operator}`);
        gAService.sendEvent(this.getCategoryFromOperator(this.operator), 'open', eventName);
        this.telemetryService.logAjaxRequest(this.getTelemetryTypeFromOperator(this.operator), -1, new Date(), matchId);

        this.prepareNextCheck();
    }

    public stop(matchId: string, operator: LiveStreamOperatorType): void {
        if (this.matchId && this.matchId === matchId && operator === this.operator) {
            var nowTime = new Date().getTime();
            this.stopRaw(nowTime);
        }
    }

    public stopRaw(nowTime: number, isOnWindowClose: boolean = false): void {
        window.clearTimeout(this.checkTimer);
        if (nowTime >= this.startedTimeTs) {
            let diff = nowTime - this.startedTimeTs;
            this.loggingService.info(`Ga timing ${this.eventName} ${this.operator} ${diff}`);
            gAService.sendTiming(this.getCategoryFromOperator(this.operator), this.eventName, diff, isOnWindowClose);
        }
        this.clearPlayedMatch();
    }

    private checkHeartbeat(): void {
        var nowTime = new Date().getTime();
        this.loggingService.info(`LiveStream heartbeat ${this.eventName} ${this.operator} ${nowTime - this.lastHeartbeatTs}`);
        if (nowTime - this.lastHeartbeatTs > 60 * 1000) {
            this.stopRaw(this.lastHeartbeatTs);
            return;
        }
        this.lastHeartbeatTs = nowTime;
        this.prepareNextCheck();
    }

    private prepareNextCheck() {
        let matchId = this.matchId;
        let operator = this.operator;
        this.checkTimer = window.setTimeout(() => {
            if (matchId === this.matchId && operator === this.operator) {
                this.checkHeartbeat();
            }
        }, 20000);
    }

    private getCategoryFromOperator(operator: LiveStreamOperatorType): string {
        switch (operator) {
            case LiveStreamOperatorType.MatchTrackerBRStage:
            case LiveStreamOperatorType.MatchTrackerBR: return "MatchTracker";
        }
        return "Video";
    }

    private getTelemetryTypeFromOperator(operator: LiveStreamOperatorType): TelemetryServiceDataType {
        switch (operator) {
            case LiveStreamOperatorType.MatchTrackerBR: return TelemetryServiceDataType.MatchTrackerShown;
        }
        return TelemetryServiceDataType.VideoShown;
    }
};
